/* eslint-disable max-lines */
import { gql } from 'graphql-tag';

import { ImageFragments } from './ImageType';
import { UserFragments } from './UserType';

const speciesId = gql`
  fragment SpeciesId on Species {
    _id: externalId
  }
`;

const fishingWaterTopSpeciesNodeFields = gql`
  fragment FishingWaterTopSpeciesNodeFields on Species {
    ...SpeciesId
    anglers: catches(filters: { fishingWaterId: $externalId }) {
      topAnglers {
        totalCount
      }
    }
    displayName
    image {
      ...ImageFields
    }
    fishrulesIds
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

const speciesTipNodeFields = gql`
  fragment SpeciesTipNodeFields on SpeciesTip {
    externalId
    tip
    userDescription
    userDisplayName
    user {
      ...UserId
      ...UserAvatar
    }
  }
  ${UserFragments.id}
  ${UserFragments.avatar}
`;

/**
 * Note: SpeciesListNodeFields depends on CatchFragments.id (CatchId) and
 * PostFragments.id (PostId). To avoid circular imports (CatchType and PostType
 * both import SpeciesTypes), those dependencies are NOT included here.
 * The query that uses this fragment must include them.
 */
const speciesListNodeFields = gql`
  fragment SpeciesListNodeFields on Species {
    ...SpeciesId
    externalId
    displayName
    catches {
      totalCount
    }
    image {
      ...ImageFields
    }
    exampleCatchWithImage {
      ...CatchId
      post {
        ...PostId
        images(first: 1, croppingStrategy: SMART, height: 280, width: 400) {
          edges {
            node {
              id
              ...ImageFields
            }
          }
        }
        webPImages: images(
          first: 1
          croppingStrategy: SMART
          height: 280
          width: 400
          format: WEBP
        ) {
          edges {
            node {
              id
              ...ImageFields
            }
          }
        }
      }
    }
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

const speciesListEdgeFields = gql`
  fragment SpeciesListEdgeFields on SpeciesEdge {
    node {
      ...SpeciesListNodeFields
    }
  }
  ${speciesListNodeFields}
`;

const speciesBasicInfoNodeFields = gql`
  fragment SpeciesBasicInfoNodeFields on Species {
    ...SpeciesId
    externalId
    displayName
    image {
      ...ImageFields
    }
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

const regionalSpeciesNodeFields = gql`
  fragment RegionalSpeciesNodeFields on Species {
    ...SpeciesId
    externalId
    displayName
    image {
      ...ImageFields
    }
    areaSpecies(stateCode: $stateCode, countryCode: $countryId) {
      recordLengthInM
      recordWeightInKg
    }
    speciesGroupings(first: 1, speciesGroupingType: SEO_SPECIES_GROUPING) {
      edges {
        node {
          externalId
          name
        }
      }
    }
    fishrulesIds
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

const speciesImageNodeFields = gql`
  fragment SpeciesImageNodeFields on Species {
    ...SpeciesId
    imageCopyrightStatus
    imageCopyrightExpiryDate
    image(width: 240, croppingStrategy: NO_CROP) {
      url
    }
    webPImage: image(width: 240, format: WEBP, croppingStrategy: NO_CROP) {
      url
    }
  }
  ${speciesId}
`;

/**
 * Note: TopSpeciesNearLocationNodeFields depends on CatchFragments.id (CatchId) and
 * PostFragments.id (PostId). To avoid circular imports (CatchType and PostType
 * both import SpeciesTypes), those dependencies are NOT included here.
 * The query that uses this fragment must include them.
 */
const topSpeciesNearLocationNodeFields = gql`
  fragment TopSpeciesNearLocationNodeFields on Species {
    ...SpeciesId
    externalId
    legacyId: id
    displayName
    image {
      ...ImageFields
    }
    exampleCatchWithImage {
      ...CatchId
      post {
        ...PostId
        images(first: 1, croppingStrategy: SMART, height: 280, width: 400) {
          edges {
            node {
              id
              ...ImageFields
            }
          }
        }
        webPImages: images(
          first: 1
          croppingStrategy: SMART
          height: 280
          width: 400
          format: WEBP
        ) {
          edges {
            node {
              id
              ...ImageFields
            }
          }
        }
      }
    }
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

/**
 * Note: SingleSpeciesFields depends on fragments from CatchType, PostType,
 * UserType, and ImageType. To avoid circular imports, those dependencies are
 * NOT included here via interpolation. The query that uses this fragment must
 * include them:
 * - CatchFragments.id, .metadata, .speciesName, .fishingWaterName
 * - PostFragments.id, .productUnits, .videoFields
 * - UserFragments.id, .avatar
 * - ImageFragments.imageFields
 * - SpeciesTipNodeFields (from this file)
 * - SpeciesCatchWithImageNodeFields (from CatchType)
 * - SpeciesExampleCatchFields (from CatchType)
 */
const singleSpeciesFields = gql`
  fragment SingleSpeciesFields on Species {
    ...SpeciesId
    legacyId: id
    followedByCurrentUser
    latinName
    displayName
    description
    maxLength
    maxWeight
    iucnStatus
    threatToHumans
    waterTypes
    minCommonDepth
    maxCommonDepth
    catches {
      totalCount
    }
    newCatchCount: catches(filters: { lastNDays: $recentCatchLimitDays }) {
      totalCount
    }
    exampleCatchWithImage {
      ...SpeciesExampleCatchFields
    }
    catchesWithImages: catches(first: 10, filters: { withPhotos: true, orderByRecent: true }) {
      edges {
        node {
          ...SpeciesCatchWithImageNodeFields
        }
      }
    }
    followers {
      totalCount
    }
    speciesTips(first: 10) {
      edges {
        node {
          ...SpeciesTipNodeFields
        }
      }
    }
  }
  ${speciesId}
`;

const placeTopSpeciesNodeFields = gql`
  fragment PlaceTopSpeciesNodeFields on Species {
    ...SpeciesId
    displayName
    image {
      ...ImageFields
    }
    fishrulesIds
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

const speciesGroupingId = gql`
  fragment SpeciesGroupingId on SpeciesGrouping {
    _id: externalId
  }
`;

const speciesGroupsInCountryNodeFields = gql`
  fragment SpeciesGroupsInCountryNodeFields on Species {
    ...SpeciesId
    displayName
    speciesGroupings(speciesGroupingType: SEO_SPECIES_GROUPING, first: 1) {
      edges {
        node {
          ...SpeciesGroupingId
          name
        }
      }
    }
  }
  ${speciesId}
  ${speciesGroupingId}
`;

const speciesByGroupNodeFields = gql`
  fragment SpeciesByGroupNodeFields on Species {
    ...SpeciesId
    externalId
    displayName
    catches {
      totalCount
    }
    image {
      ...ImageFields
    }
  }
  ${speciesId}
  ${ImageFragments.imageFields}
`;

/**
 * Fragment for the full topSpeciesSummary connection on Place.
 * Covers edges (with catchesCount) and nodes (with PlaceTopSpeciesNodeFields).
 */
const placeTopSpeciesConnectionFields = gql`
  fragment PlaceTopSpeciesConnectionFields on TopSpeciesConnection {
    edges {
      catchesCount
      node {
        ...PlaceTopSpeciesNodeFields
      }
    }
  }
  ${placeTopSpeciesNodeFields}
`;

export const SpeciesFragments = {
  id: speciesId,
  fishingWaterTopSpeciesNodeFields,
  fishingWaterTopSpeciesSummaryFields: gql`
    fragment FishingWaterTopSpeciesSummaryFields on TopSpeciesConnection {
      edges {
        node {
          ...FishingWaterTopSpeciesNodeFields
        }
      }
    }
    ${fishingWaterTopSpeciesNodeFields}
  `,
  speciesTipNodeFields,
  singleSpeciesFields,
  speciesListNodeFields,
  speciesListEdgeFields,
  speciesBasicInfoNodeFields,
  regionalSpeciesNodeFields,
  speciesImageNodeFields,
  topSpeciesNearLocationNodeFields,
  placeTopSpeciesNodeFields,
  placeTopSpeciesConnectionFields,
  speciesGroupsInCountryNodeFields,
  speciesByGroupNodeFields,
};
